import React, { useMemo, useState } from "react";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  Transition,
} from "framer-motion";

import { useLandingContent } from "../../lib/hooks/useContent";

interface FAQSectionProps {
  /** If true, multiple FAQ items can be open at once. Default: false (accordion). */
  allowMultiple?: boolean;
}

interface FAQItemProps {
  faq: { id: string; question: string; answer: string };
  isOpen: boolean;
  onToggle: () => void;
}

interface IconPlusMinusProps {
  open: boolean;
}

export default function FAQSection({ allowMultiple = false }: FAQSectionProps) {
  // NOTE: hook returns { data, loading, error, errors } in our newer version.
  // To be compatible with older hook shape, we read both `error` and `errors?.faq`.
  const { data, loading, error, errors } = useLandingContent() as any;

  const faqs: Array<{ id: string; question: string; answer: string }> =
    data?.faq ?? [];

  const hasFaqError: boolean = Boolean(errors?.faq || (error && !faqs?.length));

  // Accordion state: single open by default; optionally allow multiple.
  const [openId, setOpenId] = useState<string | null>(null);
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());

  const isOpen = useMemo(
    () => (id: string) =>
      allowMultiple ? openIds.has(id) : openId !== null && openId === id,
    [allowMultiple, openId, openIds]
  );

  function toggle(id: string) {
    if (allowMultiple) {
      setOpenIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setOpenId((prev) => (prev === id ? null : id));
    }
  }

  // Loading state
  if (loading && faqs.length === 0) {
    return (
      <section className="bg-[#080D1B] py-6 px-2 md:px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-center text-[#DEE3F2] font-bold text-4xl md:text-5xl leading-tight mb-2">
            Got Questions?
          </h2>
           <h6 className="text-sm text-[#B1BFE4] mb-10">Frequently asked <span className="text-[#2B61DE]">Questions</span>?</h6>
          <p className="text-center text-white">Loading FAQs…</p>
        </div>
      </section>
    );
  }

  // Error state (FAQ only)
  if (hasFaqError && faqs.length === 0) {
    return (
      <section className="bg-[#080D1B] py-6 px-2 md:px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-center text-[#DEE3F2] font-extrabold text-4xl md:text-6xl leading-tight mb-10">
            Got Questions?
          </h2>
        </div>
        <p className="text-center text-red-600">Failed to load FAQs.</p>
      </section>
    );
  }

  // Empty state
  if (!loading && faqs.length === 0) {
    return (
      <section className="bg-[#080D1B] py-6 px-2 md:px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-center text-[#DEE3F2] font-extrabold text-4xl md:text-6xl leading-tight mb-10">
            Got Questions?
          </h2>
          <p className="text-center text-white">
            No FAQs yet — check back soon.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="faq" className=" min-h-[60vh] bg-[#080D1B] py-6 px-2 md:px-4">
      <div className="max-w-4xl mx-auto">
       <h2 className="text-center text-[#DEE3F2] font-bold text-4xl md:text-5xl leading-tight mb-2">
            Got <span className="text-[#2B61DE]">Questions</span>?
          </h2>
           <h6 className="text-sm text-[#B1BFE4] mb-10 text-center">Frequently Asked Questions</h6>
        <div className="space-y-3">
          {faqs.map((f) => (
            <FAQItem
              key={f.id}
              faq={f}
              isOpen={isOpen(f.id)}
              onToggle={() => toggle(f.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQItem({ faq, isOpen, onToggle }: FAQItemProps) {
  const prefersReducedMotion = useReducedMotion();

  const spring: Transition = prefersReducedMotion
    ? { type: "spring", stiffness: 80, damping: 20 }
    : { type: "spring", stiffness: 300, damping: 28 };

  return (
    <motion.article layout initial={false} className="rounded-xl overflow-hidden transition-all duration-500">
      <div className=" bg-gradient-to-r from-[#1a3c8a] via-[#22B7FC] to-[#1a3c8a]
      bg-[length:200%_auto] bg-left-top
      rounded-xl shadow-inner transition-all duration-500
      hover:bg-right-bottom">
        <button
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={`faq-content-${faq.id}`}
          className="w-full flex items-center justify-between p-2 md:p-4 text-left"
        >
          <h3 className="text-lg md:text-2xl font-medium text-[#DEE3F2]">
            {faq.question}
          </h3>

          <motion.span
            layout
            className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 flex items-center justify-center"
          >
            <IconPlusMinus open={isOpen} />
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              id={`faq-content-${faq.id}`}
              key="content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={spring}
              style={{ overflow: "hidden" }}
            >
              <div className="p-2 md:p-4 pt-0 text-[#DEE3F2]">
                <p className="text-base md:text-lg leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.article>
  );
}

function IconPlusMinus({ open }: IconPlusMinusProps) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      {open ? (
        <path
          d="M6 12h12"
          stroke="#0b1730"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <>
          <path
            d="M12 6v12"
            stroke="#0b1730"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6 12h12"
            stroke="#0b1730"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )}
    </svg>
  );
}
